import { useState } from 'react';
import { Alert, Pressable, Text, TextInput, View } from 'react-native';
import type { ModId, Reg } from '../store/types';
import type { Campo, Colecao, Ctx, ModDef, TopoProps } from './tipos';
import type { Action } from '../components/ui';
import { Recolhivel } from '../components/Recolhivel';
import { excluirCaixa } from '../components/BoxCard';
import { getDoc } from '../store/store';
import { enviarListaAoPlanner, listaNoPlanner } from '../lib/enviarPlanner';
import { copiarTexto } from '../lib/copiar';
import { citacao, dadosDe, dadosDoDoi, dadosDoLink, listaDeReferencias, referencia, type Estilo } from '../lib/referencia';
import { gerarECompartilharPdf, nomeArquivo } from '../lib/pdf';
import { datasDaSituacao } from '../lib/leituraRelatorio';
import { MetaAnual, RelatorioLeitura } from '../components/RelatorioLeitura';
import { salvarECompartilhar } from '../lib/arquivoSalvar';
import { dataBr, junta, num, op, pct } from './util';
import { mudarCampos, regsDe } from '../store/modulos';
import { useDoc } from '../store/store';
import { addBox, ordenarCaixas, unarchiveBox } from '../store/anotacoes';
import { BoxCard, SwipeBox, confirmarExclusao } from '../components/BoxCard';
import { Arquivados } from '../components/Selecao';
import { Ordenavel } from '../components/Arrastar';
import { C, alpha } from '../components/theme';
import { addDays, diaCurto, parseYmd, today, weekStart } from '../lib/date';
import { N_, t } from '../lib/i18n';

const STATUS_LIVRO = [N_('Quero ler'), N_('Lendo'), N_('Lido'), N_('Parei')];
// 0.56: "Quero ver" virou "Quero assistir" e "Visto" virou "Assistido" (os antigos são acertados ao abrir a aba)
const STATUS_MIDIA = [N_('Quero assistir'), N_('Assistindo'), N_('Assistido'), N_('Parei')];
const GENERO_LIVRO = [N_('Ficção'), N_('Romance'), N_('Fantasia'), N_('Suspense'), N_('Biografia'), N_('Finanças'), N_('Negócios'),
  N_('Autoajuda'), N_('Técnico'), N_('Religião'), N_('Infantil'), N_('Outro')];
const GENERO_MIDIA = [N_('Ação'), N_('Comédia'), N_('Drama'), N_('Suspense'), N_('Sci-Fi'), N_('Terror'), N_('Romance'), N_('Animação'),
  N_('Documentário'), N_('Outro')];
const PERIODO = [N_('por dia'), N_('por semana')];

/** Progresso do livro e previsão de término pela meta de páginas por dia. */
export function progressoLivro(r: Reg) {
  const total = num(r.c.paginas), lidas = Math.min(total || Infinity, num(r.c.atual));
  const p = pct(lidas, total);
  const meta = num(r.c.meta);
  const fim = meta > 0 && total > lidas ? addDays(today(), Math.ceil((total - lidas) / meta)) : null;
  return { p, lidas, total, fim };
}
const barra = (p: number) => '▰'.repeat(Math.round(p / 10)) + '▱'.repeat(10 - Math.round(p / 10));

/** Páginas lidas hoje: a página de agora menos a página em que o dia começou. */
function lidasHoje(r: Reg) {
  const hoje = r.c.hojeData === today();
  const inicio = hoje ? num(r.c.hojeInicio) : num(r.c.atual);
  return { inicio, lidas: hoje ? Math.max(0, num(r.c.atual) - inicio) : 0 };
}

/** Guarda a página em que parou, lembrando em que página o dia começou (para contar as lidas hoje). */
export function registrarPagina(mod: ModId, r: Reg, pagina: number) {
  const hoje = today();
  const inicio = r.c.hojeData === hoje ? num(r.c.hojeInicio) : num(r.c.atual);
  const total = num(r.c.paginas);
  const atual = Math.max(0, total ? Math.min(total, pagina) : pagina);
  // 0.57: páginas lidas em cada dia (últimos 60), para a sequência de leitura e o gráfico da semana
  const log: Record<string, number> = {};
  const limite = addDays(hoje, -400);   // texto 16: guarda pouco mais de um ano (relatório do ano); a sequência e o gráfico usam só os últimos dias
  for (const [d, n] of Object.entries((r.c.log ?? {}) as Record<string, number>)) if (d >= limite) log[d] = num(n);
  log[hoje] = Math.max(0, atual - inicio);
  const novo = total && atual >= total ? 'Lido' : r.c.status !== 'Lendo' && atual > 0 ? 'Lendo' : '';
  mudarCampos(mod, r.id, {
    atual, hojeData: hoje, hojeInicio: inicio, log,
    ...(novo === 'Lido' ? { status: 'Lido', fim: r.c.fim || hoje } : novo === 'Lendo' ? { status: 'Lendo' } : {}),
    ...(novo ? datasDaSituacao('livro', r.c, novo) : {}),
  });
}

/** Páginas lidas por dia somando todos os livros. */
export function paginasPorDia(regs: Reg[]) {
  const dias: Record<string, number> = {};
  for (const r of regs) if (r.col === 'livro') for (const [d, n] of Object.entries((r.c.log ?? {}) as Record<string, number>)) dias[d] = (dias[d] ?? 0) + num(n);
  return dias;
}
/** Dias seguidos com leitura, até hoje (ou até ontem, se hoje ainda não leu). */
export function sequenciaLeitura(dias: Record<string, number>) {
  let d = today();
  if (!(dias[d] > 0)) d = addDays(d, -1);
  let n = 0;
  while (dias[d] > 0 && n < 400) { n++; d = addDays(d, -1); }
  return n;
}

/** Os últimos 7 dias em barrinhas (páginas por dia), com a linha da meta. */
function Semana({ dias, meta, cor }: { dias: Record<string, number>; meta: number; cor: string }) {
  const lista = Array.from({ length: 7 }, (_, i) => addDays(today(), i - 6));
  const maior = Math.max(meta, ...lista.map(d => dias[d] ?? 0), 1);
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 6, height: 62 }}>
      {lista.map(d => {
        const n = dias[d] ?? 0, h = Math.max(3, Math.round((n / maior) * 40));
        return (
          <View key={d} style={{ flex: 1, alignItems: 'center', gap: 3 }}>
            <Text style={{ color: C.mute, fontSize: 9 }}>{n || ''}</Text>
            <View style={{ width: '70%', height: h, borderRadius: 4, backgroundColor: n === 0 ? C.card2 : meta > 0 && n >= meta ? C.ok : cor }} />
            <Text style={{ color: d === today() ? C.text : C.mute, fontSize: 10, fontWeight: d === today() ? '800' : '500' }}>{diaCurto(parseYmd(d).getDay())}</Text>
          </View>
        );
      })}
    </View>
  );
}

/** Barra fina de progresso. */
function Barra({ p, cor }: { p: number; cor: string }) {
  return (
    <View style={{ height: 6, borderRadius: 3, backgroundColor: C.card2, overflow: 'hidden' }}>
      <View style={{ width: `${Math.max(0, Math.min(100, p))}%`, height: 6, borderRadius: 3, backgroundColor: cor }} />
    </View>
  );
}

/** Campo numérico com o botão de confirmar ("Cheguei na página..." / "Parei na aula..."). */
function Parei({ rotulo, atual, pode, cor, onOk, depois }: { rotulo: string; atual: number; pode: boolean; cor: string; onOk: (n: number) => void; depois?: string }) {
  const [v, setV] = useState('');
  const ok = () => { if (!v) return; onOk(parseInt(v, 10)); setV(''); };
  return (
    <View style={{ gap: 6 }}>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700' }}>{rotulo}</Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <TextInput editable={pode} value={v} onChangeText={x => setV(x.replace(/\D/g, '').slice(0, 6))} keyboardType="number-pad" inputAccessoryViewButtonLabel={t('Concluído')} returnKeyType="done"
          onSubmitEditing={ok} placeholder={String(atual || 0)} placeholderTextColor={C.mute} accessibilityLabel={rotulo}
          style={{ flex: 1, minWidth: 0, backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8, color: C.text, fontSize: 17, fontWeight: '800' }} />
        {!!depois && <Text style={{ color: C.sub, fontSize: 13 }}>{depois}</Text>}
        <Pressable disabled={!pode || !v} onPress={ok} accessibilityLabel={t('Confirmar')}
          style={{ width: 42, height: 40, borderRadius: 10, backgroundColor: v ? cor : C.card2, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ color: v ? '#fff' : C.mute, fontWeight: '900', fontSize: 17 }}>✓</Text>
        </Pressable>
      </View>
    </View>
  );
}

const caixa = (cor: string) => ({ flexDirection: 'row' as const, gap: 12, backgroundColor: C.card, borderRadius: 16, padding: 12, borderWidth: 1, borderColor: alpha(cor, 0.35) });

/** Card de progresso diário do livro (0.56): à esquerda, a página em que parou; à direita, as lidas hoje e a meta. */
function ProgressoDiario({ r, ctx, pode }: TopoProps) {
  const meta = num(r.c.meta), atual = num(r.c.atual), total = num(r.c.paginas);
  const { lidas, inicio } = lidasHoje(r);
  const p = meta > 0 ? Math.round((lidas / meta) * 100) : 0;
  const bateu = meta > 0 && lidas >= meta;
  const todos = paginasPorDia(ctx.regs);
  const seq = sequenciaLeitura(todos);
  const [semana, setSemana] = useState(false);
  return (
    <View style={{ ...caixa(ctx.cor), flexDirection: 'column', gap: 10 }}>
    <View style={{ flexDirection: 'row', gap: 12 }}>
      <View style={{ flex: 1, gap: 4 }}>
        <Parei rotulo={t('Cheguei na página...')} atual={atual} pode={pode} cor={ctx.cor} onOk={n => registrarPagina(ctx.mod, r, n)} />
        {!!total && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Página {a} de {n}', { a: atual, n: total })}</Text>}
      </View>
      <View style={{ width: 1, backgroundColor: C.line }} />
      <View style={{ flex: 1, gap: 4, justifyContent: 'center' }}>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700' }}>{t('Lidas hoje')}</Text>
        <Text style={{ color: C.text, fontSize: 24, fontWeight: '900' }}>{lidas} <Text style={{ fontSize: 13, fontWeight: '600', color: C.sub }}>{t('páginas')}</Text></Text>
        {lidas > 0 && <Text style={{ color: C.mute, fontSize: 11 }}>{atual} − {inicio} = {lidas}</Text>}
        {meta > 0 ? (<>
          <Barra p={p} cor={bateu ? C.ok : ctx.cor} />
          <Text style={{ color: bateu ? C.ok : C.sub, fontSize: 12, fontWeight: '700' }}>
            {bateu ? '🎉 ' + t('Meta 100% batida') : t('{p}% da meta de {m} páginas', { p, m: meta })}</Text>
          {lidas > meta && <Text style={{ color: C.ok, fontSize: 12, fontWeight: '600' }}>{t('+{n} páginas acima da meta', { n: lidas - meta })}</Text>}
        </>) : <Text style={{ color: C.mute, fontSize: 11 }}>{t('Defina a meta de páginas por dia em Editar.')}</Text>}
      </View>
    </View>
      <Pressable onPress={() => setSemana(v => !v)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, borderTopWidth: 1, borderTopColor: C.line, paddingTop: 8 }}>
        <Text style={{ color: seq ? C.text : C.sub, fontSize: 13, fontWeight: '700', flex: 1 }}>
          {seq ? '🔥 ' + t('{n} dia(s) seguido(s) lendo', { n: seq }) : '📅 ' + t('Leia hoje para começar uma sequência')}</Text>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Semana')} {semana ? '▾' : '▸'}</Text>
      </Pressable>
      {semana && <Semana dias={(r.c.log ?? {}) as Record<string, number>} meta={meta} cor={ctx.cor} />}
    </View>
  );
}

const MOTIVO_PAREI = [N_('Não gostei'), N_('Sem tempo'), N_('Difícil demais'), N_('Perdi o interesse'), N_('Outro')];
const VOLTAR = [N_('Sim, depois'), N_('Não, abandonei')];
const parei = (c: Record<string, any>) => c.status === 'Parei';
/** Texto 13 B / 15: "Por que parei" (opções rápidas + caixa livre) e se quer voltar. O mesmo em Livros, Cursos e Filmes. */
const camposParei = (voltar = N_('Quero voltar?')): Campo[] => [
  { k: 'pareiPor', r: N_('Por que parei'), t: 'opcao', op: MOTIVO_PAREI, mostrar: parei },
  { k: 'pareiTexto', r: '', t: 'longo', ph: N_('Se quiser, escreva com as suas palavras'), mostrar: parei },
  { k: 'voltar', r: voltar, t: 'opcao', op: VOLTAR, mostrar: parei },
];
const SEPARADO_ABANDONADOS = { titulo: N_('Abandonados'), icone: '📚', f: (r: Reg) => abandonado(r) };
/** Texto 14 H: "Copiar ABNT" e "Copiar APA" (no ⋮ e no cartão "Referência"). */
async function copiarRef(r: Reg, estilo: Estilo) {
  const txt = referencia(dadosDe(r), estilo);
  if (!txt) return Alert.alert(t('Referência vazia'), t('Preencha o título (e o que mais tiver) na ficha.'));
  if (await copiarTexto(txt)) Alert.alert(estilo === 'abnt' ? t('ABNT copiada') : t('APA copiada'), txt);
}
const acoesRef = (r: Reg) => [
  { label: '📚 ' + t('Copiar ABNT'), onPress: () => copiarRef(r, 'abnt') },
  { label: '📚 ' + t('Copiar APA'), onPress: () => copiarRef(r, 'apa') },
];
/** Cartão recolhido "📚 Referência" na página do livro (texto 14 H). */
function CartaoReferencia({ r, cor, dica }: { r: Reg; cor: string; dica?: string }) {
  const abnt = referencia(dadosDe(r), 'abnt');
  return (
    <Recolhivel visual="cartao" titulo={'📚 ' + t('Referência')} chave={'leitura:referencia'}>
      <Text style={{ color: C.text, fontSize: 13 }} selectable>{abnt}</Text>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        <Pressable onPress={() => copiarRef(r, 'abnt')} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 12, backgroundColor: cor }}>
          <Text style={{ color: '#fff', fontSize: 12, fontWeight: '800' }}>{t('Copiar ABNT')}</Text></Pressable>
        <Pressable onPress={() => copiarRef(r, 'apa')} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 12, borderWidth: 1, borderColor: cor }}>
          <Text style={{ color: cor, fontSize: 12, fontWeight: '800' }}>{t('Copiar APA')}</Text></Pressable>
      </View>
      <Text style={{ color: C.mute, fontSize: 11 }}>{dica ? t(dica) : t('Os dados vêm da ficha do livro (Editar › Referência).')}</Text>
    </Recolhivel>
  );
}
/** Texto 14 J: preencher a referência pelo DOI (Crossref) ou pelo link (dados que o próprio site informa). */
function PreencherPorLink({ c, mudar, cor }: { c: Record<string, any>; mudar: (p: Record<string, any>) => void; cor: string }) {
  const [busy, setBusy] = useState(false);
  const doi = String(c.doi ?? '').trim(), link = String(c.linkRef ?? '').trim();
  if (!doi && !link) return null;
  const ir = async () => {
    setBusy(true);
    try {
      const lidos = doi ? await dadosDoDoi(doi) : await dadosDoLink(link);
      // texto 21 C6: a data completa da página (notícia, blog) vai para "Data ou ano de publicação"
      const { dataPub, ...novos } = lidos;
      if (dataPub && (!novos.anoPub || /^\d{4}$/.test(String(novos.anoPub)))) novos.anoPub = dataPub;
      const vazios = Object.fromEntries(Object.entries(novos).filter(([k, v]) => v !== '' && v != null && (c[k] === '' || c[k] == null || k === 'doi')));
      if (!Object.keys(vazios).length) return Alert.alert(t('Nada novo'), t('Não achei dados novos para completar a ficha.'));
      mudar({ ...vazios, ...(link && !c.acesso ? { acesso: today() } : {}) });
      Alert.alert(t('Ficha preenchida'), t('Confira os campos antes de salvar.'));
    } catch (e: any) {
      Alert.alert(t('Não foi possível preencher'), t(String(e?.message ?? e)));
    } finally { setBusy(false); }
  };
  return (
    <Pressable onPress={ir} disabled={busy} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, backgroundColor: alpha(cor, 0.12) }}>
      <Text style={{ fontSize: 18 }}>🔗</Text>
      <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>{busy ? t('Buscando os dados...') : doi ? t('Preencher pelo DOI') : t('Preencher pelo link')}</Text>
    </Pressable>
  );
}
/** Texto 13 B: parou e não quer voltar: vai para a linha recolhida "Abandonados", no fim da lista. */
export const abandonado = (r: Reg) => r.c.status === 'Parei' && r.c.voltar === 'Não, abandonei';

/** Topo da página do livro: o progresso do dia e o cartão da referência (texto 14 H). */
function TopoLivro(p: TopoProps) {
  return <><ProgressoDiario {...p} /><CartaoReferencia r={p.r} cor={p.ctx.cor} /></>;
}

const livro: Colecao = {
  id: 'livro', nome: N_('Livros'), um: N_('Livro'), icone: r => (r.c.status === 'Lido' ? '📗' : r.c.status === 'Lendo' ? '📖' : '📚'),
  campos: [
    { k: 'titulo', r: N_('Título'), t: 'texto' },
    { k: 'autor', r: N_('Autor'), t: 'texto', dica: N_('Mais de um autor: separe com ponto e vírgula (;).'), ia: 'todos os autores, separados por ";"' },
    { k: 'genero', r: N_('Gênero ou categoria'), t: 'opcao', op: GENERO_LIVRO, outro: true },
    { k: 'status', r: N_('Situação'), t: 'opcao', op: STATUS_LIVRO },
    // texto 13 B: ao marcar "Parei", o motivo (opções rápidas + caixa livre) e se quer voltar
    ...camposParei(N_('Quero voltar a ler?')),
    { k: 'paginas', r: N_('Total de páginas'), t: 'numero' },
    { k: 'capitulos', r: N_('Total de capítulos'), t: 'numero' },
    { k: 'atual', r: N_('Página em que estou'), t: 'numero' },
    { k: 'meta', r: N_('Meta de páginas por dia'), t: 'numero' },
    // texto 13 C: só as datas que combinam com a situação (o que já foi preenchido continua guardado)
    { k: 'inicio', r: N_('Comecei em'), t: 'data', mostrar: c => c.status === 'Lendo' || c.status === 'Lido' || c.status === 'Parei' },
    { k: 'fim', r: N_('Terminei em'), t: 'data', mostrar: c => c.status === 'Lido' },
    { k: 'parada', r: N_('Parei em'), t: 'data', mostrar: parei },
    { k: 'nota', r: N_('Avaliação'), t: 'estrelas' },
    { k: 'resumo', r: N_('Resumo e opinião'), t: 'longo' },
    // texto 14 H: referência (opcional, recolhida). Título, autor(es) e total de páginas vêm dos campos acima.
    { k: 'subtitulo', r: N_('Subtítulo'), t: 'texto' },
    { k: 'organizador', r: N_('Organizador'), t: 'texto' },
    { k: 'tradutor', r: N_('Tradutor'), t: 'texto' },
    { k: 'edicao', r: N_('Edição'), t: 'numero', ia: 'só o número da edição (ex.: 2 para "2ª edição")' },
    { k: 'volume', r: N_('Volume'), t: 'numero' },
    { k: 'editora', r: N_('Editora'), t: 'texto' },
    { k: 'cidade', r: N_('Cidade'), t: 'texto', ia: 'cidade da editora (local de publicação)' },
    { k: 'anoPub', r: N_('Ano ou data de publicação'), t: 'texto', ia: 'ano de publicação (ou a data, se for artigo)' },
    { k: 'isbn', r: N_('ISBN'), t: 'texto' },
    { k: 'linkRef', r: N_('Link (material online)'), t: 'link' },
    { k: 'acesso', r: N_('Data de acesso'), t: 'data', mostrar: c => !!String(c.linkRef ?? '').trim() },
    { k: 'doi', r: N_('DOI (artigos)'), t: 'texto', ph: N_('Ex.: 10.1590/...') },
  ],
  // texto 13 D: os números juntos, num bloco que dá para ocultar; "Página em que estou" no topo
  blocos: [{ titulo: N_('Páginas e meta'), campos: ['atual', 'paginas', 'capitulos', 'meta'] },
    { titulo: N_('Referência'), fechado: true, campos: ['subtitulo', 'organizador', 'tradutor', 'edicao', 'volume', 'editora', 'cidade', 'anoPub', 'isbn', 'linkRef', 'acesso', 'doi'] }],
  extraFicha: PreencherPorLink,
  nomeGrupo: N_('Grupo de leitura'),
  textoGrupo: N_('Crie o grupo e mande o link: quem entrar vê o material do livro e acompanha a leitura. Quem administra inclui, edita e põe fotos, e pode liberar isso para quem também usa a aba. Só quem administra coloca ou tira pessoas.'),
  tituloNovo: N_('Novo livro'), tituloEditar: N_('Editar livro'), subCompleto: true,
  situacao: { campo: 'status', datas: (c, novo) => datasDaSituacao('livro', c, novo) },
  topoLista: p => <MetaAnual tipo="livro" cor={p.ctx.cor} />,
  separado: SEPARADO_ABANDONADOS,
  // gêneros usados nos livros (inclusive os escritos em "Outro") viram filtro
  filtrosDe: l => [...new Set(l.map(r => String(r.c.genero ?? '')).filter(g => g && g !== 'Outro'))]
    .sort((a, b) => t(a).localeCompare(t(b)))
    .map(g => [g, (r: Reg) => r.c.genero === g] as [string, (r: Reg) => boolean]),
  titulo: r => r.c.titulo || '',
  sub: r => { const g = progressoLivro(r); return junta(r.c.autor, op(r.c.genero), g.total ? `${barra(g.p)} ${g.p}%` : '', r.c.status === 'Lendo' && g.fim && t('termina em {d}', { d: dataBr(g.fim) }), num(r.c.nota) && '★'.repeat(num(r.c.nota))); },
  ordem: (a, b) => { const o = ['Lendo', 'Quero ler', 'Parei', 'Lido']; return o.indexOf(a.c.status) - o.indexOf(b.c.status) || String(a.c.titulo).localeCompare(String(b.c.titulo)); },
  feito: r => r.c.status === 'Lido', rapido: 'status', anexos: true, porFoto: N_('Cadastrar pela foto da capa'),
  novo: () => ({ status: 'Lendo', inicio: today(), atual: 0 }),
  ajustar: (c, k) => {
    if (k === 'atual' && num(c.paginas) && num(c.atual) >= num(c.paginas)) return { ...c, status: 'Lido', fim: c.fim || today() };
    if (k === 'status' && c.status === 'Lido' && !c.fim) return { ...c, fim: today(), atual: c.paginas || c.atual };
    return c;
  },
  acoes: (r, ctx, menu) => [...[5, 10, 20, 50].map(n => ({ label: '📖 ' + t('Li mais {n} páginas', { n }), onPress: () => registrarPagina(ctx.mod, r, num(r.c.atual) + n) })),
    ...acoesRef(r), ...acoesPasta(r, ctx, menu)],
  filtros: STATUS_LIVRO.map(s => [s, r => r.c.status === s] as [string, (r: Reg) => boolean]),
  resumo: l => {
    const ano = today().slice(0, 4); const n = l.filter(r => r.c.status === 'Lido' && String(r.c.fim || '').startsWith(ano)).length;
    const dias = paginasPorDia(l), hoje = dias[today()] ?? 0, seq = sequenciaLeitura(dias);
    return junta(hoje > 0 && t('Hoje: {n} página(s)', { n: hoje }), seq > 1 && '🔥 ' + t('{n} dia(s) seguido(s) lendo', { n: seq }), n > 0 && t('{n} livro(s) lido(s) este ano', { n })) || null;
  },
  vazio: N_('Cadastre o livro com o total de páginas e a meta por dia. Toque em ⋮ para somar as páginas lidas.'),
  painelTopo: TopoLivro, grupoRecolhido: true, filhosTitulo: N_('Minhas anotações do livro'),
};

const TIPO_NOTA = [N_('Trecho (citação)'), N_('Ideia minha')];
const ehTrecho = (r: Reg) => r.c.tipo !== 'Ideia minha';
/** Texto 14 I: copia o trecho entre aspas com a indicação pronta (autor, ano e página), pela referência do livro. */
async function copiarCitacao(r: Reg, ctx: Ctx, estilo: Estilo) {
  const livroR = ctx.porId(r.pai);
  const trecho = String(r.c.texto ?? '').trim();
  if (!livroR || !trecho) return Alert.alert(t('Trecho vazio'), t('Escreva o trecho na anotação para copiar a citação.'));
  const txt = citacao(trecho, dadosDe(livroR), num(r.c.pagina), estilo);
  if (await copiarTexto(txt)) Alert.alert(t('Citação copiada'), txt);
}
const nota: Colecao = {
  id: 'nota', pai: 'livro', nome: N_('Anotações'), um: N_('Anotação'), icone: r => (ehTrecho(r) ? '❝' : '💡'),
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPO_NOTA },
    { k: 'pagina', r: N_('Página'), t: 'numero' },
    { k: 'texto', r: N_('Trecho ou ideia'), t: 'longo' },
  ],
  titulo: r => (num(r.c.pagina) ? t('Página {n}', { n: num(r.c.pagina) }) : t('Anotação')), sub: r => String(r.c.texto || '').slice(0, 140),
  ordem: (a, b) => num(a.c.pagina) - num(b.c.pagina), anexos: true, porFoto: N_('Anotar pela foto da página'),
  novo: () => ({ tipo: 'Trecho (citação)' }),
  acoes: (r, ctx) => (ehTrecho(r) ? [
    { label: '❝ ' + t('Copiar citação (ABNT)'), onPress: () => copiarCitacao(r, ctx, 'abnt') },
    { label: '❝ ' + t('Copiar citação (APA)'), onPress: () => copiarCitacao(r, ctx, 'apa') },
  ] : []),
};

// ---------- artigos e sites (texto 21, Parte C): as mesmas peças de Livros ----------
const TIPO_ARTIGO = [N_('Artigo científico'), N_('Artigo de site ou blog'), N_('Notícia'), N_('Capítulo de livro'), N_('Outro')];
const ehWeb = (c: Record<string, any>) => c.tipo === 'Artigo de site ou blog' || c.tipo === 'Notícia';
/** Topo da página do artigo: o cartão "📚 Referência" (Copiar ABNT e Copiar APA). */
function TopoArtigo({ r, ctx }: TopoProps) {
  return <CartaoReferencia r={r} cor={ctx.cor} dica={N_('Os dados vêm da ficha do artigo (Editar).')} />;
}
const artigo: Colecao = {
  id: 'artigo', nome: N_('Artigos e sites'), um: N_('Artigo ou site'),
  icone: r => (r.c.status === 'Lido' ? '✅' : ehWeb(r.c) ? '🌐' : r.c.tipo === 'Capítulo de livro' ? '📑' : '📰'),
  campos: [
    { k: 'titulo', r: N_('Título'), t: 'texto' },
    { k: 'autor', r: N_('Autor(es)'), t: 'texto', dica: N_('Mais de um autor: separe com ponto e vírgula (;).'), ia: 'todos os autores, separados por ";"' },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPO_ARTIGO, outro: true },
    { k: 'veiculo', r: N_('Revista, site ou veículo'), t: 'texto', sugestoes: true, ia: 'nome da revista científica, do jornal ou do site' },
    { k: 'anoPub', r: N_('Data ou ano de publicação'), t: 'texto', ph: N_('Ex.: 2024 ou 12/03/2024'), ia: 'data de publicação (dd/mm/aaaa) ou só o ano' },
    { k: 'linkRef', r: N_('Link'), t: 'link' },
    { k: 'doi', r: N_('DOI'), t: 'texto', ph: N_('Ex.: 10.1590/...') },
    { k: 'paginasArt', r: N_('Páginas (opcional)'), t: 'texto', ph: N_('Ex.: 45-60') },
    { k: 'acesso', r: N_('Data de acesso'), t: 'data' },
    { k: 'status', r: N_('Situação'), t: 'opcao', op: STATUS_LIVRO },
    ...camposParei(N_('Quero voltar a ler?')),
    { k: 'inicio', r: N_('Comecei em'), t: 'data', mostrar: c => c.status === 'Lendo' || c.status === 'Lido' || c.status === 'Parei' },
    { k: 'fim', r: N_('Terminei em'), t: 'data', mostrar: c => c.status === 'Lido' },
    { k: 'parada', r: N_('Parei em'), t: 'data', mostrar: parei },
    { k: 'nota', r: N_('Avaliação'), t: 'estrelas' },
    { k: 'resumo', r: N_('Resumo e opinião'), t: 'longo' },
  ],
  extraFicha: PreencherPorLink,
  nomeGrupo: N_('Grupo de artigos'),
  textoGrupo: N_('Crie o grupo e mande o link: quem entrar vê os artigos e acompanha a leitura. Quem administra inclui, edita e põe fotos, e pode liberar isso para quem também usa a aba. Só quem administra coloca ou tira pessoas.'),
  tituloNovo: N_('Novo artigo ou site'), tituloEditar: N_('Editar artigo ou site'), subCompleto: true,
  situacao: { campo: 'status', datas: (c, novo) => datasDaSituacao('artigo', c, novo) },
  separado: SEPARADO_ABANDONADOS,
  titulo: r => r.c.titulo || '',
  sub: r => junta(r.c.autor, op(r.c.tipo), r.c.veiculo, r.c.anoPub, num(r.c.nota) && '★'.repeat(num(r.c.nota))),
  ordem: (a, b) => { const o = ['Lendo', 'Quero ler', 'Parei', 'Lido']; return o.indexOf(a.c.status) - o.indexOf(b.c.status) || String(a.c.titulo).localeCompare(String(b.c.titulo)); },
  feito: r => r.c.status === 'Lido', rapido: 'status', anexos: true, porFoto: N_('Cadastrar pela foto ou PDF'),
  // C2: a data de acesso vem com o dia em que o link foi salvo (dá para mudar)
  novo: () => ({ status: 'Quero ler', acesso: today() }),
  aoRapido: (_r, novo) => (novo === 'Parei' ? { abrir: true } : {}),
  acoes: (r, ctx, menu) => [...acoesRef(r), ...acoesPasta(r, ctx, menu)],
  filtros: STATUS_LIVRO.map(s => [s, r => r.c.status === s] as [string, (r: Reg) => boolean]),
  filtrosDe: l => [...new Set(l.map(r => String(r.c.tipo ?? '')).filter(g => g && g !== 'Outro'))]
    .sort((a, b) => t(a).localeCompare(t(b)))
    .map(g => [g, (r: Reg) => r.c.tipo === g] as [string, (r: Reg) => boolean]),
  vazio: N_('Guarde artigos, notícias e páginas da internet com a referência pronta (ABNT e APA). Preencha pelo DOI ou pelo link.'),
  painelTopo: TopoArtigo, grupoRecolhido: true, filhosTitulo: N_('Minhas anotações do artigo'),
};
/** Anotações do artigo: as mesmas do livro (trecho com página e ideia minha), com "Copiar citação". */
const notaArtigo: Colecao = { ...nota, id: 'notaArtigo', pai: 'artigo' };

const ehSerie = (c: Record<string, any>) => c.tipo === 'Série' || c.tipo === 'Anime';
/** Texto 15 C5: porcentagem da série (temporadas × episódios por temporada) ou do podcast (total de episódios). */
export function progressoMidia(c: Record<string, any>): number | null {
  const ep = num(c.episodio), tp = Math.max(1, num(c.temporada) || 1);
  if (ehSerie(c) && num(c.totTemporadas) && num(c.epsTemp)) return pct((tp - 1) * num(c.epsTemp) + ep, num(c.totTemporadas) * num(c.epsTemp));
  if (c.tipo === 'Podcast' && num(c.totEp)) return pct(ep, num(c.totEp));
  return null;
}
const TIPO_MIDIA = [N_('Filme'), N_('Série'), N_('Anime'), N_('Documentário'), N_('Podcast'), N_('Outro')];
const midia: Colecao = {
  id: 'midia', nome: N_('Filmes e séries'), um: N_('Filme ou série'),
  icone: r => (ehSerie(r.c) ? '📺' : r.c.tipo === 'Documentário' ? '🎞️' : r.c.tipo === 'Podcast' ? '🎧' : '🎬'),
  campos: [
    { k: 'titulo', r: N_('Título'), t: 'texto' },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPO_MIDIA, outro: true },
    { k: 'genero', r: N_('Gênero ou categoria'), t: 'opcao', op: GENERO_MIDIA, outro: true },
    { k: 'ano', r: N_('Ano de lançamento'), t: 'numero', ano: true },
    { k: 'onde', r: N_('Onde assistir'), t: 'texto', ph: N_('Ex.: Netflix, cinema'), sugestoes: true },
    { k: 'status', r: N_('Situação'), t: 'opcao', op: STATUS_MIDIA },
    ...camposParei(),
    // texto 15 C5: em que temporada e episódio está (e os totais, se quiser, para a porcentagem)
    { k: 'temporada', r: N_('Cheguei na temporada'), t: 'numero', mostrar: ehSerie },
    { k: 'episodio', r: N_('Cheguei no episódio'), t: 'numero', mostrar: c => ehSerie(c) || c.tipo === 'Podcast' },
    { k: 'totTemporadas', r: N_('Total de temporadas (opcional)'), t: 'numero', mostrar: ehSerie },
    { k: 'epsTemp', r: N_('Episódios por temporada (opcional)'), t: 'numero', mostrar: ehSerie },
    { k: 'totEp', r: N_('Total de episódios (opcional)'), t: 'numero', mostrar: c => c.tipo === 'Podcast' },
    // texto 15 C4: só as datas que combinam com a situação
    { k: 'inicio', r: N_('Comecei em'), t: 'data', mostrar: c => c.status === 'Assistindo' || (c.status === 'Assistido' && ehSerie(c)) },
    { k: 'assistido', r: N_('Assisti em'), t: 'data', mostrar: c => c.status === 'Assistido' },
    { k: 'parada', r: N_('Parei em'), t: 'data', mostrar: parei },
    { k: 'nota', r: N_('Avaliação'), t: 'estrelas' },
    { k: 'notas', r: N_('Comentários'), t: 'longo' },
    // texto 15 C7: referência (opcional, recolhida); o ano é o de lançamento, lá em cima
    { k: 'direcao', r: N_('Direção'), t: 'texto' },
    { k: 'produtora', r: N_('Produtora ou país'), t: 'texto' },
  ],
  blocos: [{ titulo: N_('Referência'), fechado: true, campos: ['direcao', 'produtora'] }],
  titulo: r => r.c.titulo || '',
  sub: r => { const pm = progressoMidia(r.c); return junta(op(r.c.tipo), op(r.c.genero), num(r.c.ano) && String(num(r.c.ano)), r.c.onde,
    num(r.c.temporada) && `T${num(r.c.temporada)}${num(r.c.episodio) ? 'E' + num(r.c.episodio) : ''}`, !num(r.c.temporada) && num(r.c.episodio) && `E${num(r.c.episodio)}`,
    pm != null && `${barra(pm)} ${pm}%`, num(r.c.nota) && '★'.repeat(num(r.c.nota))); },
  ordem: 'titulo', feito: r => r.c.status === 'Assistido' || r.c.status === 'Visto', rapido: 'status', anexos: true,
  novo: () => ({ status: 'Quero assistir' }),
  // texto 15 C4: a folha de situação do cartão continua igual; só acrescenta as datas e, no "Parei", abre para o motivo
  aoRapido: (_r, novo) => (novo === 'Parei' ? { abrir: true } : {}),
  situacao: { campo: 'status', datas: (c, novo) => datasDaSituacao('midia', c, novo) },
  separado: SEPARADO_ABANDONADOS,
  topoLista: p => <MetaAnual tipo="midia" cor={p.ctx.cor} />,
  acoes: (r, ctx, menu) => [...(ehSerie(r.c) ? [{ label: '▶ ' + t('Vi mais um episódio'), onPress: () => mudarCampos(ctx.mod, r.id, { episodio: num(r.c.episodio) + 1, status: 'Assistindo', ...(r.c.inicio ? {} : { inicio: today() }) }) }] : []),
    ...acoesRef(r), ...acoesPasta(r, ctx, menu)],
  filtros: STATUS_MIDIA.map(s => [s, r => r.c.status === s] as [string, (r: Reg) => boolean]),
  nomeGrupo: N_('Grupo de filmes e séries'),
  textoGrupo: N_('Crie o grupo e mande o link: quem entrar vê a lista e acompanha. Quem administra inclui, edita e põe fotos, e pode liberar isso para quem também usa a aba. Só quem administra coloca ou tira pessoas.'),
};

// ---------- cursos, divididos em matérias (0.56) ----------
const materiasDe = (curso: Reg, regs: Reg[]) => regs.filter(x => x.pai === curso.id && x.col === 'materia');

/** Progresso geral do curso: sem módulos, as aulas assistidas do curso; com módulos (texto 15 B4.6b), a soma dos módulos
 *  mais as aulas "avulsas" (assistidas antes do primeiro módulo e deixadas fora deles, texto 15 B1). */
export function progressoCurso(r: Reg, regs: Reg[]) {
  const ms = materiasDe(r, regs);
  if (!ms.length) { const aulas = num(r.c.aulas), feitas = num(r.c.feitas); return { aulas, feitas, p: pct(feitas, aulas), ms }; }
  const avulsas = num(r.c.avulsas);
  const aulas = ms.reduce((s, m) => s + num(m.c.aulas), 0) + avulsas;
  const feitas = ms.reduce((s, m) => s + Math.min(num(m.c.aulas) || Infinity, num(m.c.atual)), 0) + avulsas;
  return { aulas, feitas, p: pct(feitas, aulas), ms };
}
const inicioSemana = () => weekStart(today());
/** Aulas feitas hoje ou nesta semana (pelo registro diário do curso). */
function aulasNoPeriodo(r: Reg, semana: boolean) {
  const de = semana ? inicioSemana() : today();
  return Object.entries((r.c.log ?? {}) as Record<string, number>).filter(([d]) => d >= de).reduce((s, [, n]) => s + num(n), 0);
}
/** Atualiza as aulas feitas do curso e o registro do dia (para a meta). delta = aulas a mais (ou a menos) agora. */
function atualizarCurso(mod: ModId, cursoId: string, delta: number) {
  const regs = regsDe(mod);
  const c = regs.find(x => x.id === cursoId);
  if (!c) return;
  const g = progressoCurso(c, regs);
  const log: Record<string, number> = {};
  const limite = addDays(today(), -400);   // texto 16: pouco mais de um ano, para o relatório do ano
  for (const [d, n] of Object.entries((c.c.log ?? {}) as Record<string, number>)) if (d >= limite) log[d] = num(n);
  if (delta) log[today()] = Math.max(0, num(log[today()]) + delta);
  // texto 15 B4.6b: com módulos, os totais do curso são calculados por eles (aparecem travados no Editar curso)
  mudarCampos(mod, c.id, { log, ...(g.ms.length ? { feitas: g.feitas, aulas: g.aulas, modulos: g.ms.length } : {}) });
}
/** Texto 15 B1: o primeiro módulo de um curso que já tem aulas assistidas não apaga essas aulas. Elas ficam guardadas
 *  como "avulsas" (somando no curso) e o app pergunta em qual módulo colocar. */
function primeiroModulo(mod: ModId, m: Reg) {
  const regs = regsDe(mod);
  const c = regs.find(x => x.id === m.pai);
  if (!c || c.c.avulsas != null) return;
  const ms = materiasDe(c, regs);
  const antes = num(c.c.feitas);
  if (ms.length !== 1 || antes <= 0) { mudarCampos(mod, c.id, { avulsas: 0 }); return; }
  mudarCampos(mod, c.id, { avulsas: antes });   // nada se perde, mesmo se a pessoa fechar a pergunta
  setTimeout(() => Alert.alert(t('Você já assistiu {n} aula(s). Colocar em qual módulo?', { n: antes }), t('Se deixar avulsas, elas continuam somando no total do curso.'), [
    ...ms.map(x => ({ text: String(x.c.nome || t('Módulo')), onPress: () => moverAvulsas(mod, c.id, x.id) })),
    { text: t('Deixar avulsas'), style: 'cancel' as const },
  ]), 700);
}
function moverAvulsas(mod: ModId, cursoId: string, mid: string) {
  const regs = regsDe(mod);
  const c = regs.find(x => x.id === cursoId), m = regs.find(x => x.id === mid);
  if (!c || !m) return;
  const av = num(c.c.avulsas);
  const cabe = num(m.c.aulas) ? Math.max(0, num(m.c.aulas) - num(m.c.atual)) : av;
  const n = Math.min(av, cabe);
  mudarCampos(mod, m.id, { atual: num(m.c.atual) + n });
  mudarCampos(mod, c.id, { avulsas: av - n });
  atualizarCurso(mod, cursoId, 0);
}
/** "Cheguei na aula X de Y" da matéria: atualiza a matéria e o progresso geral do curso. */
export function registrarAula(mod: ModId, m: Reg, aula: number) {
  const tot = num(m.c.aulas);
  const atual = Math.max(0, tot ? Math.min(tot, aula) : aula);
  const delta = atual - num(m.c.atual);
  mudarCampos(mod, m.id, { atual });
  if (m.pai) atualizarCurso(mod, m.pai, delta);
}
function maisUmaAula(mod: ModId, r: Reg) {
  mudarCampos(mod, r.id, { feitas: Math.min(num(r.c.aulas) || Infinity, num(r.c.feitas) + 1) });
  atualizarCurso(mod, r.id, 1);
}

/** Topo da página do curso: progresso geral, meta de aulas e previsão. */
function ProgressoCurso({ r, ctx, pode }: TopoProps) {
  const g = progressoCurso(r, ctx.regs);
  const meta = num(r.c.metaAulas), semana = r.c.metaPeriodo === 'por semana';
  const feitasPer = aulasNoPeriodo(r, semana);
  const pm = meta > 0 ? Math.round((feitasPer / meta) * 100) : 0;
  const faltam = Math.max(0, g.aulas - g.feitas);
  const fim = meta > 0 && faltam > 0 ? addDays(today(), Math.ceil(faltam / meta) * (semana ? 7 : 1) - (semana ? 1 : 0)) : null;
  return (
    <View style={{ ...caixa(ctx.cor), flexDirection: 'column', gap: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>🎯 {t('Progresso geral')}</Text>
        <Text style={{ color: C.text, fontSize: 20, fontWeight: '900' }}>{g.p}%</Text>
      </View>
      <Barra p={g.p} cor={g.p >= 100 ? C.ok : ctx.cor} />
      <Text style={{ color: C.sub, fontSize: 12 }}>{junta(g.aulas ? t('{a} de {n} aulas', { a: g.feitas, n: g.aulas }) : t('{a} aula(s) feita(s)', { a: g.feitas }),
        g.ms.length ? t('{n} matéria(s)', { n: g.ms.length }) : num(r.c.modulos) ? t('{n} módulo(s)', { n: num(r.c.modulos) }) : '')}</Text>
      {meta > 0 && (
        <View style={{ gap: 4 }}>
          <Text style={{ color: feitasPer >= meta ? C.ok : C.sub, fontSize: 12, fontWeight: '700' }}>
            {feitasPer >= meta ? '🎉 ' + t('Meta de aulas batida') : (semana ? t('Esta semana: {a} de {m} aulas ({p}%)', { a: feitasPer, m: meta, p: pm }) : t('Hoje: {a} de {m} aulas ({p}%)', { a: feitasPer, m: meta, p: pm }))}</Text>
          {!!fim && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Nesse ritmo, termina em {d}', { d: dataBr(fim) })}</Text>}
        </View>
      )}
      {!meta && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Defina a meta de aulas por dia ou por semana em Editar.')}</Text>}
      {pode && !g.ms.length && (
        <Pressable onPress={() => maisUmaAula(ctx.mod, r)} style={{ alignSelf: 'flex-start', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 12, backgroundColor: alpha(ctx.cor, 0.2) }}>
          <Text style={{ color: C.text, fontSize: 12, fontWeight: '700' }}>✅ {t('Assisti mais uma aula')}</Text>
        </Pressable>
      )}
    </View>
  );
}

const STATUS_CURSO = [N_('Quero fazer'), N_('Fazendo'), N_('Concluído'), N_('Parei')];
const temModulos = (c: Record<string, any>, ctx: Ctx, reg: Reg) => (ctx.filhos(reg.id, 'materia').length ? N_('Calculado pelos módulos') : null);
const cursoFeito = (r: Reg) => r.c.status === 'Concluído' || (num(r.c.aulas) > 0 && num(r.c.feitas) >= num(r.c.aulas));
const temCertificado = (r: Reg) => !!r.c.certData || (r.anexos ?? []).some(a => a.nome?.startsWith(t('Certificado') + ' · '));
const curso: Colecao = {
  id: 'curso', nome: N_('Cursos'), um: N_('Curso'), icone: r => (cursoFeito(r) ? '🎓' : '🧑‍💻'),
  campos: [
    { k: 'nome', r: N_('Curso'), t: 'texto' },
    { k: 'plataforma', r: N_('Plataforma ou escola'), t: 'texto' },
    { k: 'instrutor', r: N_('Instrutor'), t: 'texto' },
    { k: 'link', r: N_('Link'), t: 'link' },
    { k: 'status', r: N_('Situação'), t: 'opcao', op: STATUS_CURSO },
    ...camposParei(),
    // texto 15 B4.6b: com módulos, estes três são a soma dos módulos (travados)
    { k: 'aulas', r: N_('Total de aulas'), t: 'numero', calculado: temModulos },
    { k: 'modulos', r: N_('Total de módulos'), t: 'numero', calculado: temModulos },
    { k: 'feitas', r: N_('Aulas assistidas'), t: 'numero', calculado: temModulos },
    { k: 'cargaHoraria', r: N_('Carga horária (horas)'), t: 'numero' },
    { k: 'metaAulas', r: N_('Meta de aulas'), t: 'numero', comOpcao: 'metaPeriodo' },
    { k: 'metaPeriodo', r: N_('Período da meta'), t: 'opcao', op: PERIODO },
    { k: 'prazo', r: N_('Terminar até'), t: 'data', aviso: [7, 1], atalhosMeses: [1, 3] },
    // texto 15 B4.2: só as datas que combinam com a situação
    { k: 'inicio', r: N_('Comecei em'), t: 'data', mostrar: c => c.status === 'Fazendo' || c.status === 'Concluído' || c.status === 'Parei' },
    { k: 'fim', r: N_('Terminei em'), t: 'data', mostrar: c => c.status === 'Concluído' },
    { k: 'parada', r: N_('Parei em'), t: 'data', mostrar: parei },
    { k: 'certData', r: N_('Certificado emitido em'), t: 'data', dica: N_('A foto ou o PDF do certificado vai em "Fotos, arquivos e links": marque como Certificado.') },
    { k: 'nota', r: N_('Avaliação'), t: 'estrelas' },
    { k: 'comentario', r: N_('Comentário'), t: 'longo' },
    { k: 'notas', r: N_('Anotações'), t: 'longo' },
  ],
  marcas: [N_('Certificado')],
  titulo: r => r.c.nome || '',
  sub: (r, ctx) => {
    const g = progressoCurso(r, ctx.regs);
    const meta = num(r.c.metaAulas), semana = r.c.metaPeriodo === 'por semana', faltam = Math.max(0, g.aulas - g.feitas);
    const fim = r.c.status !== 'Concluído' && meta > 0 && faltam > 0 ? addDays(today(), Math.ceil(faltam / meta) * (semana ? 7 : 1) - (semana ? 1 : 0)) : null;
    return junta(r.c.plataforma, g.aulas && `${barra(g.p)} ${g.feitas}/${g.aulas}`, fim && t('termina em {d}', { d: dataBr(fim) }), temCertificado(r) && '🎓 ' + t('Certificado'),
      num(r.c.nota) && '★'.repeat(num(r.c.nota)));
  },
  data: 'prazo', feito: cursoFeito, anexos: true, porFoto: N_('Cadastrar pela foto (página do curso, certificado, folheto)'),
  rapido: 'status',
  // texto 15 B4.7 e C4: o botão de situação do cartão também acerta as datas; no "Parei", abre para o motivo
  aoRapido: (_r, novo) => (novo === 'Parei' ? { abrir: true } : {}),
  situacao: { campo: 'status', datas: (c, novo) => datasDaSituacao('curso', c, novo) },
  separado: SEPARADO_ABANDONADOS,
  topoLista: p => <MetaAnual tipo="curso" cor={p.ctx.cor} />,
  novo: () => ({ feitas: 0, metaPeriodo: 'por semana', status: 'Fazendo', inicio: today() }),
  acoes: (r, ctx, menu) => [...(ctx.filhos(r.id, 'materia').length ? [] : [{ label: '✅ ' + t('Assisti mais uma aula'), onPress: () => maisUmaAula(ctx.mod, r) }]),
    ...acoesRef(r), ...acoesPasta(r, ctx, menu)],
  agenda: r => ({ title: t('Estudar: {c}', { c: r.c.nome }), recur: { type: 'weekly' }, start: today(), end: r.c.prazo || null }),
  // texto 15 B2: sem o cartão "Enviar a lista inteira": o envio é pelo ⋮ de cada curso, só quando a pessoa manda
  semEnviarLista: true,
  painelTopo: ProgressoCurso, grupoRecolhido: true,
  nomeGrupo: N_('Grupo do curso'),
  textoGrupo: N_('Crie o grupo e mande o link: quem entrar vê o material do curso e acompanha. Quem administra inclui, edita e põe fotos, e pode liberar isso para quem também usa a aba. Só quem administra coloca ou tira pessoas.'),
};

/** Cards de anotações de uma matéria: as mesmas caixas de Anotações (checklist, lista, texto, desenho), presas à matéria. */
function CardsDaMateria({ r, ctx, pode }: { r: Reg; ctx: Ctx; pode: boolean }) {
  const { boxes } = useDoc('anotacoes');
  const cards = boxes.filter(b => b.ref === r.id && !b.archivedAt);
  const arquivados = boxes.filter(b => b.ref === r.id && !!b.archivedAt);
  return (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', letterSpacing: 0.8, flex: 1 }}>{t('CARDS DE ANOTAÇÕES')} ({cards.length})</Text>
        {pode && <Pressable onPress={() => addBox('', 'leitura', r.id)} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 12, backgroundColor: ctx.cor }}>
          <Text style={{ color: '#fff', fontSize: 12, fontWeight: '800' }}>+ {t('Card')}</Text></Pressable>}
      </View>
      {!cards.length && <Text style={{ color: C.mute, fontSize: 13 }}>{t('Crie cards com checklist, lista, texto livre ou desenho para esta matéria. Arraste para a direita para excluir e para a esquerda para Arquivar ou Duplicar.')}</Text>}
      <Ordenavel itens={cards} chave={b => b.id} alca gap={10} onOrdem={ordenarCaixas}
        render={(b, _i, a) => <SwipeBox box={b}><BoxCard box={b} alca={a.alca} /></SwipeBox>} />
      <Arquivados itens={arquivados} chave={b => b.id} cor={ctx.cor} render={b => <Text style={{ color: C.text }} numberOfLines={1}>{b.title || t('Sem título')}</Text>}
        onRestaurar={b => unarchiveBox(b.id)} onExcluir={pode ? b => confirmarExclusao(b) : undefined} />
    </View>
  );
}

/** Topo da página da matéria: "Cheguei na aula X de Y", o progresso e os cards de anotações. */
function PainelMateria({ r, ctx, pode }: TopoProps) {
  const tot = num(r.c.aulas), atual = num(r.c.atual), p = pct(atual, tot);
  return (
    <View style={{ gap: 10 }}>
      <View style={{ ...caixa(ctx.cor), flexDirection: 'column', gap: 8 }}>
        <Parei rotulo={t('Cheguei na aula...')} atual={atual} pode={pode} cor={ctx.cor} depois={tot ? t('de {n}', { n: tot }) : undefined} onOk={n => registrarAula(ctx.mod, r, n)} />
        {!!tot && <Barra p={p} cor={p >= 100 ? C.ok : ctx.cor} />}
        <Text style={{ color: C.sub, fontSize: 12 }}>{tot ? t('Aula {a} de {n} ({p}%)', { a: atual, n: tot, p }) : t('Informe o total de aulas da matéria em Editar.')}</Text>
      </View>
      <CardsDaMateria r={r} ctx={ctx} pode={pode} />
    </View>
  );
}

const materia: Colecao = {
  id: 'materia', pai: 'curso', nome: N_('Matérias'), um: N_('Matéria ou módulo'), icone: r => (num(r.c.aulas) && num(r.c.atual) >= num(r.c.aulas) ? '✅' : '📘'),
  campos: [
    { k: 'nome', r: N_('Matéria ou módulo'), t: 'texto' },
    { k: 'aulas', r: N_('Total de aulas'), t: 'numero' },
    { k: 'atual', r: N_('Cheguei na aula'), t: 'numero' },
  ],
  titulo: r => r.c.nome || '',
  // texto 15 B3.3: "aula 5 de 10" fica junto (espaços que não quebram a linha)
  sub: r => (num(r.c.aulas) ? `${barra(pct(num(r.c.atual), num(r.c.aulas)))} ${t('aula {a} de {n}', { a: num(r.c.atual), n: num(r.c.aulas) }).replace(/ /g, '\u00A0')}` : ''),
  feito: r => num(r.c.aulas) > 0 && num(r.c.atual) >= num(r.c.aulas),
  novo: () => ({ atual: 0 }), anexos: true, pagina: true,
  aoSalvar: (r, ctx) => { if (!r.pai) return; primeiroModulo(ctx.mod, r); atualizarCurso(ctx.mod, r.pai, 0); },
  // texto 15 B3.4: o Excluir diz o que vai junto; com conteúdo, pede também a senha
  avisoExcluir: r => { const n = cardsDe(r).length; return n ? t('Os {n} card(s) de anotações deste módulo também vão para a lixeira.', { n }) : t('O módulo não tem cards de anotações.'); },
  grande: r => cardsDe(r).length > 0 || num(r.c.atual) > 0 || !!String(r.c.notas ?? '').trim(),
  aoExcluir: (r, ctx) => { cardsDe(r).forEach(excluirCaixa); if (r.pai) setTimeout(() => atualizarCurso(ctx.mod, r.pai!, 0), 300); },
  acoes: (r, ctx) => [{ label: '✅ ' + t('Assisti mais uma aula'), onPress: () => registrarAula(ctx.mod, r, num(r.c.atual) + 1) }],
  vazio: N_('Divida o curso em matérias ou módulos. Em cada uma, marque em que aula parou e crie cards de anotações.'),
  painelTopo: PainelMateria,
};

// ---------- pastas (texto 14, Parte K) ----------
// A pasta guarda ATALHOS (os ids das fichas), não cópias: editar o livro muda em todas as pastas, e excluir a pasta
// não apaga nada. Um item pode estar em várias pastas.
const PODE_PASTA = ['livro', 'artigo', 'midia', 'curso'];   // texto 21 C7: artigos também
const idsDaPasta = (p: Reg): string[] => (Array.isArray(p.c.itens) ? p.c.itens : []);
export const itensDaPasta = (p: Reg, ctx: Ctx) => idsDaPasta(p).map(id => ctx.porId(id)).filter((r): r is Reg => !!r && PODE_PASTA.includes(r.col));
const pastasDe = (ctx: Ctx) => ctx.regs.filter(r => r.col === 'pasta');
function alternarNaPasta(mod: ModId, p: Reg, id: string, por: boolean) {
  const l = idsDaPasta(p).filter(x => x !== id);
  mudarCampos(mod, p.id, { itens: por ? [...l, id] : l });
}
/** "Colocar em pasta" e "Tirar da pasta" no ⋮ de livros, filmes e cursos. */
function acoesPasta(r: Reg, ctx: Ctx, menu?: (titulo: string, a: Action[]) => void): Action[] {
  if (!menu) return [];
  const todas = pastasDe(ctx), dentro = todas.filter(p => idsDaPasta(p).includes(r.id));
  const nome = (p: Reg) => `${p.c.icone || '📁'} ${p.c.nome || t('Pasta')}`;
  const out: Action[] = [{ label: '📁 ' + t('Colocar em pasta'), onPress: () => {
    const fora = todas.filter(p => !dentro.includes(p));
    if (!fora.length) return Alert.alert(t('Colocar em pasta'), todas.length ? t('Este item já está em todas as pastas.') : t('Crie uma pasta primeiro, na parte 📁 Pastas desta aba.'));
    menu(t('Colocar em pasta'), fora.map(p => ({ label: nome(p), onPress: () => alternarNaPasta(ctx.mod, p, r.id, true) })));
  } }];
  if (dentro.length) out.push({ label: '📂 ' + t('Tirar da pasta'), onPress: () => menu(t('Tirar da pasta'), dentro.map(p => ({ label: nome(p), onPress: () => alternarNaPasta(ctx.mod, p, r.id, false) }))) });
  return out;
}

/** Na pasta: a descrição e a "Lista de referências" (ABNT ou APA) para copiar ou exportar em PDF ou Word. */
function TopoPasta({ r, ctx }: TopoProps) {
  const [estilo, setEstilo] = useState<Estilo>('abnt');
  const itens = itensDaPasta(r, ctx);
  const lista = listaDeReferencias(itens, estilo);
  const cor = String(r.c.cor || ctx.cor);
  const nome = String(r.c.nome || t('Pasta'));
  const titulo = `${t('Referências')} · ${nome}`;
  const copiar = async () => { if (await copiarTexto(lista.join('\n\n'))) Alert.alert(t('Lista copiada'), t('{n} referência(s) em ordem alfabética.', { n: lista.length })); };
  const pdf = () => gerarECompartilharPdf(titulo, [{ t: 'titulo', texto: t('Referências') }, { t: 'sub', texto: nome }, { t: 'espaco' },
    ...lista.map(x => ({ t: 'par' as const, texto: x })), ]).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e)));
  const word = () => {
    const esc = (x: string) => x.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<html><head><meta charset="utf-8"><title>${esc(titulo)}</title></head><body style="font-family:'Times New Roman';font-size:12pt">`
      + `<h2 style="text-align:center">${esc(t('Referências'))}</h2>` + lista.map(x => `<p style="text-align:justify;margin:0 0 12pt 0">${esc(x)}</p>`).join('') + '</body></html>';
    salvarECompartilhar(new TextEncoder().encode(html), nomeArquivo(titulo) + '.doc', 'application/msword').catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e)));
  };
  return (
    <View style={{ ...caixa(cor), flexDirection: 'column', gap: 8 }}>
      {!!String(r.c.descricao ?? '').trim() && <Text style={{ color: C.text, fontSize: 13 }}>{String(r.c.descricao)}</Text>}
      {!!r.c.prazo && <Text style={{ color: C.sub, fontSize: 12 }}>📅 {t('Prazo: {d}', { d: dataBr(r.c.prazo) })}</Text>}
      <Recolhivel visual="linha" titulo={'📚 ' + t('Lista de referências') + ` (${lista.length})`}>
        {!lista.length ? <Text style={{ color: C.mute, fontSize: 12 }}>{t('Coloque livros, filmes ou cursos nesta pasta para montar a lista.')}</Text> : (<>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {(['abnt', 'apa'] as Estilo[]).map(e => (
              <Pressable key={e} onPress={() => setEstilo(e)} style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14, backgroundColor: estilo === e ? cor : C.card2 }}>
                <Text style={{ color: estilo === e ? '#fff' : C.text, fontSize: 12, fontWeight: '800' }}>{e.toUpperCase()}</Text></Pressable>))}
          </View>
          {lista.map((x, i) => <Text key={i} style={{ color: C.text, fontSize: 12 }} selectable>{x}</Text>)}
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            {[[t('Copiar'), copiar], ['PDF', pdf], ['Word', word]].map(([l, f]) => (
              <Pressable key={l as string} onPress={f as () => void} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 12, borderWidth: 1, borderColor: cor }}>
                <Text style={{ color: cor, fontSize: 12, fontWeight: '800' }}>{l as string}</Text></Pressable>))}
          </View>
        </>)}
      </Recolhivel>
    </View>
  );
}

const pasta: Colecao = {
  id: 'pasta', nome: N_('Pastas'), um: N_('Pasta'), icone: r => r.c.icone || '📁',
  campos: [
    { k: 'nome', r: N_('Nome da pasta'), t: 'texto', ph: N_('Ex.: TCC — Ética e Direito') },
    { k: 'icone', r: N_('Ícone'), t: 'icone' },
    { k: 'cor', r: N_('Cor'), t: 'cor' },
    { k: 'descricao', r: N_('Descrição (objetivo)'), t: 'longo', simples: true },
    { k: 'prazo', r: N_('Prazo'), t: 'data' },
  ],
  titulo: r => r.c.nome || '',
  sub: (r, ctx) => junta(t('{n} item(ns)', { n: itensDaPasta(r, ctx).length }), String(r.c.descricao ?? '').trim().slice(0, 80), r.c.prazo && t('Prazo: {d}', { d: dataBr(r.c.prazo) })),
  novo: () => ({ icone: '📁', itens: [] }),
  // excluir a pasta não apaga os itens (são atalhos)
  avisoExcluir: () => t('Os livros, filmes e cursos da pasta NÃO são apagados: continuam na aba.'),
  vazio: N_('Junte livros, filmes e cursos numa pasta (ex.: "TCC", "Só romances"). O mesmo item pode estar em várias pastas.'),
  painelTopo: TopoPasta, tituloNovo: N_('Nova pasta'), tituloEditar: N_('Editar pasta'),
};
/** Dentro da pasta: os atalhos (as fichas continuam na sua parte da aba). Arrastar para excluir oferece "Tirar daqui". */
const pastaItem: Colecao = {
  id: 'pastaItem', pai: 'pasta', nome: N_('Itens da pasta'), um: N_('Item'), icone: '📎', campos: [], titulo: () => '', semNovo: true,
  incluir: (ctx, pai) => (pai ? itensDaPasta(pai, ctx) : []),
  tirarIncluido: (r, ctx, pai) => { if (pai) alternarNaPasta(ctx.mod, pai, r.id, false); },
  // texto 24 (5): segurar e arrastar reordena os itens dentro da pasta (a ordem vale só nesta pasta)
  reordenarIncluidos: (ids, ctx, pai) => { if (pai) mudarCampos(ctx.mod, pai.id, { itens: [...ids, ...idsDaPasta(pai).filter(x => !ids.includes(x))] }); },
  vazioTitulo: N_('Pasta vazia'), vazio: N_('No ⋮ de um livro, filme ou curso, toque em "Colocar em pasta".'),
};
/** Cards de anotações do módulo (Anotações, presos ao módulo). */
const cardsDe = (r: Reg) => getDoc('anotacoes').boxes.filter(b => b.ref === r.id);

export const LEITURA: ModDef = {
  id: 'leitura', colecoes: [livro, nota, artigo, notaArtigo, midia, curso, materia, pasta, pastaItem],
  relatorio: RelatorioLeitura,
  migrar: regs => {
    // texto 15 B2: a lista de cursos não vai mais inteira para o My Planner (envio só pelo ⋮ de cada curso)
    if (listaNoPlanner(getDoc('ajustes'), 'leitura', 'curso')) enviarListaAoPlanner('leitura', 'curso', false);
    const out: { id: string; c: Record<string, any> }[] = regs.filter(r => r.col === 'midia' && (r.c.status === 'Quero ver' || r.c.status === 'Visto'))
      .map(r => ({ id: r.id, c: { status: r.c.status === 'Visto' ? 'Assistido' : 'Quero assistir' } }));
    for (const r of regs.filter(x => x.col === 'curso')) {
      const c: Record<string, any> = {};
      // texto 15 B4.1: cursos antigos ganham a situação
      if (!r.c.status) c.status = cursoFeito(r) ? 'Concluído' : 'Fazendo';
      // texto 15 B1: recupera as aulas assistidas que sumiram ao criar o 1º módulo (pelo registro dos últimos 60 dias)
      if (r.c.avulsas == null) {
        const ms = regs.filter(x => x.pai === r.id && x.col === 'materia');
        if (ms.length) {
          const doLog = Object.values((r.c.log ?? {}) as Record<string, number>).reduce((a, n) => a + num(n), 0);
          const nosModulos = ms.reduce((a, m) => a + Math.min(num(m.c.aulas) || Infinity, num(m.c.atual)), 0);
          c.avulsas = Math.max(0, doLog - nosModulos);
        }
      }
      if (Object.keys(c).length) out.push({ id: r.id, c });
    }
    return out;
  },
};
